// @ts-check

/** @file Durable offline-grant acquisition, verification, and expiry handling. Fetches a signed offline grant from the backend sync bootstrap endpoint, verifies it with the configured HMAC signing keys, persists it for offline use, and re-acquires it on expiry. The durable Option/transport defaults are overridable so the acquire/verify/re-acquire decision is testable without a database. */

import {verifyOfflineGrant} from "velocious/build/src/sync/offline-grant.js"

import {verifyOfflineGrantWithoutWebCrypto} from "./offline-grant-hmac-fallback.js"
import Option from "../models/option.js"
import {flushLocalDatabaseWrites} from "../shared/local-database-persistence.js"

/** @typedef {import("velocious/build/src/sync/offline-grant.js").OfflineGrant} OfflineGrant */
/** @typedef {import("velocious/build/src/sync/offline-grant.js").OfflineGrantSigningKey} OfflineGrantSigningKey */
/** @typedef {import("velocious/build/src/sync/offline-grant.js").SignedOfflineGrant} SignedOfflineGrant */
/** @typedef {() => Promise<SignedOfflineGrant | null>} LoadStoredGrant */
/** @typedef {(signedGrant: SignedOfflineGrant) => Promise<void>} StoreGrant */
/** @typedef {(deviceId: string) => Promise<SignedOfflineGrant>} FetchGrant */

/** Option-table key for the durable signed-grant envelope. */
const GRANT_STORAGE_KEY = "project-sync-offline-grant-v1"

/** Path of the backend offline-grant bootstrap endpoint (STC-13). */
const OFFLINE_GRANT_ENDPOINT = "/velocious/sync/offline-grant"

/**
 * Fetches a fresh signed offline grant from the backend bootstrap endpoint.
 * @param {string} deviceId Stable installation device id.
 * @param {{signal?: AbortSignal}} [options] Optional cancellation state.
 * @returns {Promise<SignedOfflineGrant>} Signed grant envelope.
 * @rejects {Error} When the session, transport, or endpoint validation fails.
 */
export async function fetchOfflineGrant(deviceId, options = {}) {
  // Imported lazily: the transport chain drags in the React Native platform
  // module, which plain-Node unit tests must not load.
  const {authenticatedSyncPost} = await import("../database/project-replica-transport.js")
  // Grant bootstrap is an auth call, not a sync-transport request: its
  // success must not flip the connectivity gate that callers (e.g. the
  // offline replica probe) deliberately hold offline.
  const result = await authenticatedSyncPost(OFFLINE_GRANT_ENDPOINT, {deviceId}, {...options, reportConnectivity: false})
  const payload = /** @type {{status?: string, offlineGrant?: SignedOfflineGrant}} */ (result.json())

  if (!payload || payload.status !== "success" || !payload.offlineGrant) {
    throw new Error(`Offline grant endpoint returned no usable grant (status=${payload?.status ?? "unknown"})`)
  }

  return payload.offlineGrant
}

/**
 * Verifies a signed offline grant against the configured signing keys and a
 * trusted clock, returning the trusted grant payload. Uses WebCrypto when the
 * runtime exposes it; in insecure contexts (the Android Chrome system tests
 * reach the app over the unencrypted emulator host address) crypto.subtle is
 * absent, so a pure-JS fallback reproduces the same HMAC contract instead of
 * failing the grant verification with sync-failed.
 * @param {object} args Verification inputs.
 * @param {SignedOfflineGrant} args.signedGrant Envelope to verify.
 * @param {OfflineGrantSigningKey[]} args.signingKeys Candidate verification keys.
 * @param {() => Date} [args.now] Trusted verification clock.
 * @returns {Promise<OfflineGrant>} Verified grant payload.
 * @rejects {Error} When the signature, key id, or TTL check fails.
 */
export async function verifyGrantEnvelope({signedGrant, signingKeys, now = () => new Date()}) {
  if (globalThis.crypto?.subtle) {
    return await verifyOfflineGrant({now: now(), signedGrant, signingKeys})
  }

  return verifyOfflineGrantWithoutWebCrypto({now, signedGrant, signingKeys})
}

/**
 * Reads the durable grant envelope from the local Option store.
 * @returns {Promise<SignedOfflineGrant | null>} Stored envelope.
 */
export async function loadStoredGrant() {
  const option = await Option.findBy({key: GRANT_STORAGE_KEY})
  const raw = option?.value()

  if (!raw) return null

  try {
    return /** @type {SignedOfflineGrant} */ (JSON.parse(raw))
  } catch {
    // A corrupt envelope is unusable; treat it as absent so acquisition recovers.
    return null
  }
}

/**
 * Persists a verified grant envelope durably in the local Option store.
 * @param {SignedOfflineGrant} signedGrant Envelope to store.
 * @returns {Promise<void>} Completion after the write is durable.
 */
export async function storeGrant(signedGrant) {
  const option = await Option.findOrInitializeBy({key: GRANT_STORAGE_KEY})
  option.setValue(JSON.stringify(signedGrant))
  await option.save()
  await flushLocalDatabaseWrites()
}

/**
 * Clears the durable grant envelope (e.g. On sign-out).
 * @returns {Promise<void>} Completion after the row is removed.
 */
export async function clearStoredGrant() {
  const option = await Option.findBy({key: GRANT_STORAGE_KEY})
  if (option) await option.destroy()
  await flushLocalDatabaseWrites()
}

/**
 * Resolves a verified offline grant, reusing a stored grant while it is still
 * valid and re-acquiring from the backend on first use or expiry. Storage and
 * fetch are injectable so the acquire/verify/re-acquire decision is testable
 * without a database or network.
 * @param {object} args Acquisition inputs.
 * @param {string} args.deviceId Stable installation device id.
 * @param {OfflineGrantSigningKey[]} args.signingKeys Verification keys.
 * @param {() => Date} [args.now] Trusted clock for expiry checks.
 * @param {{fetch?: FetchGrant, loadStored?: LoadStoredGrant, store?: StoreGrant, force?: boolean}} [deps] Injectable seams and controls.
 * @returns {Promise<{grant: OfflineGrant, signedGrant: SignedOfflineGrant, acquired: boolean}>} Resolved grant.
 * @rejects {Error} When acquisition or verification fails.
 */
export async function acquireOfflineGrant(
  {deviceId, signingKeys, now = () => new Date()},
  {fetch, loadStored, store, force = false} = {}
) {
  if (!deviceId) throw new Error("Offline grant acquisition requires a device id")
  if (!Array.isArray(signingKeys) || signingKeys.length < 1) throw new Error("Offline grant acquisition requires at least one signing key")

  const load = loadStored ?? loadStoredGrant
  const persist = store ?? storeGrant
  const retrieve = fetch ?? (async (id) => await fetchOfflineGrant(id))

  if (!force) {
    const stored = await load()
    if (stored) {
      try {
        const grant = await verifyGrantEnvelope({now, signedGrant: stored, signingKeys})
        return {acquired: false, grant, signedGrant: stored}
      } catch {
        // Stored grant is expired or unverified — fall through to re-acquisition.
      }
    }
  }

  const signedGrant = await retrieve(deviceId)
  const grant = await verifyGrantEnvelope({now, signedGrant, signingKeys})
  await persist(signedGrant)

  return {acquired: true, grant, signedGrant}
}

/**
 * Reports whether a stored grant is present and currently verifiable.
 * @param {object} args Inspection inputs.
 * @param {OfflineGrantSigningKey[]} args.signingKeys Verification keys.
 * @param {() => Date} [args.now] Trusted clock.
 * @returns {Promise<boolean>} Whether a valid stored grant exists.
 */
export async function hasValidStoredGrant({signingKeys, now = () => new Date()}) {
  const stored = await loadStoredGrant()
  if (!stored) return false

  try {
    await verifyGrantEnvelope({now, signedGrant: stored, signingKeys})
    return true
  } catch {
    return false
  }
}
